<template>
  <div class="card listing-card">
    <form @submit.prevent class="needs-validation" novalidate>
      <div v-if="serverError" class="alert alert-danger" role="alert">
        {{serverError}}
      </div>
      <div class="card-body">
        <div class="label-text">
          <span v-if="!event.id">Add Event</span>
          <span v-if="event.id">Edit Event</span>
        </div>
        <hr/>
        <div class="form-group row" v-if="!isUserAdminAndEvent(event.eventTypeId) && event.id">
          <div class="col-lg-4 label-text">
            Event Type
            </div>
          <div class="col-lg-8">
            {{getEventTypeName(event.eventTypeId)}}
          </div>
        </div>
        <div v-else class="form-group row">
          <label for="eventType" class="col-lg-4 label-text">
            <span class="text-danger">*</span>Event Type
          </label>
          <div class="loading col-lg-5" v-if="eventTypesLoading">
            Loading...
          </div>
          <div class="col-lg-8">
            <select v-if="!eventTypesLoading"
                    v-validate="'required'"
                    data-vv-as="Event Type"
                    class="form-control"
                    name="eventType"
                    v-model="event.eventTypeId">
              <option value="" disabled>Select Event Type</option>
              <option v-for="option in filteredEventTypes"
                      :key="option.id"
                      :value="option.id">
              {{ option.name }}
              </option>
            </select>
            <div class="invalid-feedback">{{ errors.first('eventType') }}</div>
          </div>
        </div>
        <div class="form-group row" v-if="!isUserAdminAndEvent(event.eventTypeId) && event.id">
          <div class="col-lg-4 label-text">
            Event Date
            </div>
          <div class="col-lg-8">
            {{event.date | dateString}}
          </div>
        </div>
        <div v-else class="form-group row">
          <label for="eventDate" class="col-lg-4 col-form-label label-text">
            <span class="text-danger">*</span>Event Date
          </label>
          <div class="col-lg-8">
            <datepicker
              id='eventDate'
              :class="'flex-grow-1'"
              format="MM/dd/yyyy"
              input-class="form-control bg-white"
              :use-utc="false"
              :disabled-dates="disabledDateConfig.disabledDates"
              v-model="event.date"
              @focusin.native="setEventDateIfNull"
              v-validate="'required'"
              data-vv-as="Event Date"
              name='eventDate'
              :bootstrap-styling="true">
              <div slot="afterDateInput" class="input-group-append">
                <span class="input-group-text">
                  <font-awesome-icon icon="calendar-alt" />
                </span>
              </div>
            </datepicker>
            <div class="test-invalid">{{ errors.first('eventDate')}}</div>
          </div>
        </div>
        <div class="form-group row">
          <label for="eventNotes" class="col-lg-4 col-form-label label-text">
            Event Notes
          </label>
          <div class="col-lg-8">
            <textarea data-vv-as="Notes"
                      type="textarea"
                      class="form-control"
                      name="notes"
                      v-model="event.notes"
                      placeholder="Enter Notes"
                      rows="2"
                      maxlength="500" />
              <small v-if="!errors.first('notes')" class="form-text text-muted">
                Max 500 characters
              </small>
              <div class="invalid-feedback">{{ errors.first('notes') }}</div>
          </div>
        </div>
      </div>
      <div class='card-footer right-align-content'>
        <b-button
          :disabled="submitting"
          variant="danger" class="button-row-spacing"
          v-b-modal="'cancelEventConfirmation-' + event.id">
          Cancel
        </b-button>
        <b-button
          :disabled="submitting"
          @click="submitWrapper(event)">
          Save
        </b-button>
      </div>
    </form>
    <CancelModal
      :modalId="'cancelEventConfirmation-' + event.id"
      @cancelled="cancelEdit">
    </CancelModal>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import Datepicker from 'vuejs-datepicker';
import FormSubmit from '../../mixins/FormSubmit';
import CancelModal from '../CancelModal.vue';
import EventDateHelper from '../../mixins/EventDateHelper';

export default {
  name: 'EventForm',
  components: {
    Datepicker,
    CancelModal,
  },
  mixins: [FormSubmit, EventDateHelper],
  props: [
    'event',
    'eventTypes',
    'eventTypesLoading',
    'storeNamespace',
  ],

  computed: {
    submitting() {
      return this.$store.state[this.storeNamespace].submitting;
    },
    serverError() {
      return this.$store.state[this.storeNamespace].serverError;
    },
    filteredEventTypes() {
      return this.getFilteredEventTypes();
    },
    ...mapState('User', {
      user: state => state.user,
    }),
  },
  data() {
    return {
      disabledDateConfig: {
        disabledDates: {
          from: this.getDisabledDate(),
        },
      },
      openDate: this.getEventDate(),
    };
  },
  methods: {
    cancelEdit() {
      this.$emit('closeForm', this.event.id);
    },
    submitWrapper(entity) {
      this.onSubmitToStore(
        entity,
        this.storeNamespace,
        'event',
        [],
        this.cleanUpState,
      );
    },
    cleanUpState() {
      if (this.event.instrumentId) {
        this.$store.dispatch('Instrument/forceFetchInstruments', this.$store.state.CurrentProject.currentProject.id);
      } else if (this.event.serviceId) {
        this.$store.dispatch('Service/forceFetchServicesWithNoLoadingIndicator', parseInt(this.$route.params.project_id, 10));
      }
    },
    getDisabledDate() {
      return new Date(Date.now() + 1);
    },
    isUserAdminAndEvent(eventTypeId) {
      if (!eventTypeId) {
        return false;
      }
      // Initial Record Entry Event. Do not allow edits to type OR date
      if (eventTypeId === 53) {
        return false;
      }
      const eventTypeAdmin = this.eventTypes.filter(p => p.id === eventTypeId)[0];
      if (!this.user.idToken.roles.includes('Admin') && eventTypeAdmin.adminDeleteOnly) {
        return false;
      }
      return true;
    },
    getEventTypeName(id) {
      const eventType = this.eventTypes.filter(p => p.id === id)[0];
      return eventType ? eventType.name : 'None';
    },
    setEventDateIfNull() {
      if (this.event.date === null) {
        this.event.date = this.openDate;
      }
    },
    getFilteredEventTypes() {
      if (!this.eventTypes) return [];
      if (this.user.idToken.roles.includes('Admin')) {
        return this.eventTypes;
      }

      return this.eventTypes.filter(p => p.adminEditOnly === false);
    },
  },
};
</script>
<style>
  .test-invalid {
      width: 100%;
      margin-top: 0.25rem;
      font-size: 80%;
      color: #E32222;
  }
</style>
